import type { ServiceRequestDto } from '@smartsash/api-client';
import { usePermission } from '@smartsash/permissions';
import { Link } from '@tanstack/react-router';
import classNames from 'classnames';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';

import { isAssignable } from './assignment/requestAssignment';
import { requestSubject } from './requestSubject';
import styles from './ServiceRequests.module.css';
import { LazyCardActions, LazyRequestAssignee } from './serviceRequestsLazy';

import {
  formatRequestDateTime,
  personName,
  typeLabelKey,
} from '@/ui/pages/ServiceRequestDetails/serviceRequestLabels';
import { StatusBadge } from '@/ui/pages/ServiceRequestDetails/StatusBadge';
import { EMPTY_VALUE } from '@/utils/emptyValue';

/**
 * One request below the breakpoint (HS-1548): type as the link, status, the subject when there is
 * one (HS-1542 step 14), requester, institution,
 * assignee (HS-1542: a full-width picker on an open request for a `serviceRequest.assign` caller,
 * text otherwise) and date.
 */
function MobileCard({ row }: { row: ServiceRequestDto }) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const typeKey = typeLabelKey(row.type);
  const subject = requestSubject(row);
  const assigneeText =
    row.assignee == null ? (
      <dd className={styles.unassigned}>{t('serviceRequests.assignee.unassigned')}</dd>
    ) : (
      <dd>{personName(row.assignee) ?? t('serviceRequests.unknownUser')}</dd>
    );

  return (
    <div className={styles['m-card']}>
      <div className={styles['m-card-head']}>
        <Link to="/requests/$id" params={{ id: row.id }} className={styles.link}>
          {typeKey === undefined ? row.type : t(typeKey)}
        </Link>
        <StatusBadge status={row.status} />
      </div>
      {subject !== null && <p className={styles['m-card-subject']}>{subject}</p>}
      <dl className={styles['m-card-meta']}>
        <div className={styles['m-card-row']}>
          <dt>{t('serviceRequests.columns.requester')}</dt>
          <dd>{personName(row.requester) ?? t('serviceRequests.unknownUser')}</dd>
        </div>
        <div className={styles['m-card-row']}>
          <dt>{t('serviceRequests.columns.institution')}</dt>
          <dd>{row.institutionName ?? EMPTY_VALUE}</dd>
        </div>
        {isAssignable(row, can('serviceRequest.assign')) ? (
          <div className={classNames(styles['m-card-row'], styles['m-card-row-stacked'])}>
            <dt>{t('serviceRequests.columns.assignee')}</dt>
            <Suspense fallback={assigneeText}>
              <dd>
                <LazyRequestAssignee request={row} />
              </dd>
            </Suspense>
          </div>
        ) : (
          <div className={styles['m-card-row']}>
            <dt>{t('serviceRequests.columns.assignee')}</dt>
            {assigneeText}
          </div>
        )}
        <div className={styles['m-card-row']}>
          <dt>{t('serviceRequests.columns.createdAt')}</dt>
          <dd>{formatRequestDateTime(row.createdAt, i18n.language)}</dd>
        </div>
      </dl>
      <Suspense fallback={null}>
        <LazyCardActions row={row} />
      </Suspense>
    </div>
  );
}

export default MobileCard;
